/**
 * Geocoding helpers for every map and address form. They call our backend
 * (`/api/property/geo`, see geoApi.js), which answers from Google Maps, with the
 * key kept on the server, or from OpenStreetMap:
 *
 *  - `provider: "google"` (the default): screens whose map is a Google map.
 *  - `provider: "osm"`: the Survey / Measure / Crowd Data maps, which stay on
 *    Esri imagery because Google's terms forbid drawing boundaries over its
 *    imagery and showing its places on a non-Google map.
 *
 * The backend also falls back to OpenStreetMap by itself when Google is
 * unavailable or today's spend ceiling is reached.
 *
 * The address shape and function signatures are unchanged from the old
 * browser-side Nominatim version, so the forms and save payloads don't change.
 */
import { geoApi, newSearchSession } from "./geoApi";

export { newSearchSession };

/**
 * Turn a Nominatim result (`/reverse` object or one `/search` row) into the
 * address shape the app stores everywhere:
 *   { addressLine1, city, pincode, state, country, formatted, location }
 * `location` is GeoJSON `{ type: "Point", coordinates: [lng, lat] }` to match
 * what the backend already expects.
 */
export function parseBoundingBox(bb) {
  if (!Array.isArray(bb) || bb.length < 4) return null;
  const [south, north, west, east] = bb.map(Number);
  if (![south, north, west, east].every(Number.isFinite)) return null;
  return { south, north, west, east };
}

export function parseNominatimAddress(result, lat, lng) {
  const a = result?.address || {};

  const city =
    a.city ||
    a.town ||
    a.village ||
    a.municipality ||
    a.suburb ||
    a.city_district ||
    a.county ||
    a.state_district ||
    "";

  const pincode = a.postcode || "";
  const state = a.state || a.region || "";
  const country = a.country || "";

  // Indian land-record hierarchy, best-effort from OSM fields (not every point
  // carries all three). District = revenue district; Mandal = sub-district /
  // taluk / tehsil; Village = the smallest named place.
  const district = a.state_district || a.district || a.county || "";
  const mandal =
    a.subdistrict ||
    a.taluk ||
    a.tehsil ||
    a.municipality ||
    a.city_district ||
    (a.county && a.county !== district ? a.county : "") ||
    "";
  const village =
    a.village || a.hamlet || a.town || a.suburb || a.neighbourhood || "";

  const line = [a.house_number, a.road, a.neighbourhood, a.suburb, a.hamlet]
    .filter(Boolean)
    .join(", ");

  const addressLine1 =
    line ||
    (result?.display_name
      ? result.display_name.split(",").slice(0, 2).join(", ").trim()
      : "");

  return {
    addressLine1,
    city,
    pincode,
    state,
    country,
    district,
    mandal,
    village,
    // What OSM says the pinned feature IS (`class`/`type`, e.g.
    // landuse/farmland, place/village, building/house). Carried through so a
    // caller can tell farmland from a plot without a second lookup — see
    // `inferLandType`. Absent on results that carry no classification.
    placeClass: result?.class || "",
    placeType: result?.type || "",
    // The feature's extent, as Nominatim's [south, north, west, east] strings
    // turned into numbers. Lets a map outline the area the point belongs to.
    // Null when the result carries none.
    bbox: parseBoundingBox(result?.boundingbox),
    formatted: result?.display_name || addressLine1,
    location: {
      type: "Point",
      coordinates: [Number(lng), Number(lat)],
    },
  };
}

// OSM land-use / place values that mean the pinned feature is farmland. Anything
// outside this set is NOT treated as evidence either way — a building or a
// residential plot says "non-agricultural", but so does the very common case of
// OSM knowing nothing useful about the point, and those two must not be
// conflated. Hence the null return below.
const FARM_LANDUSE = new Set([
  "farmland",
  "farmyard",
  "orchard",
  "vineyard",
  "meadow",
  "allotments",
  "greenhouse_horticulture",
  "aquaculture",
  "plant_nursery",
  "paddy",
]);

/**
 * The property type (`agriculture` | `non-agriculture`) a reverse-geocoded point
 * implies, or **null when the point doesn't say**.
 *
 * Used by the map-request flow to avoid asking a question the map can often
 * answer. Deliberately conservative: only an explicit farmland classification
 * (or an OSM `farm` address part) counts as agriculture, and only an explicitly
 * built-up classification counts as non-agriculture. Everything else — bare
 * land, an unmapped field, a point OSM has no feature for — returns null so the
 * caller falls back to its configured default rather than guessing wrong.
 *
 * @param {object|null} addr - a `parseNominatimAddress` result
 * @returns {"agriculture"|"non-agriculture"|null}
 */
export function inferLandType(addr) {
  if (!addr) return null;
  const cls = String(addr.placeClass || "").toLowerCase();
  const type = String(addr.placeType || "").toLowerCase();

  if (cls === "landuse" && FARM_LANDUSE.has(type)) return "agriculture";
  if (cls === "place" && (type === "farm" || type === "isolated_dwelling")) {
    return "agriculture";
  }
  // A built feature is unambiguous the other way.
  if (cls === "building" || cls === "highway" || cls === "shop") {
    return "non-agriculture";
  }
  if (
    cls === "landuse" &&
    ["residential", "commercial", "industrial", "retail", "construction"].includes(
      type
    )
  ) {
    return "non-agriculture";
  }
  return null;
}

/**
 * Reverse geocode a coordinate to the app address shape. Returns `null` on
 * failure so callers can no-op instead of crashing.
 *
 * `admin: true` also asks for the revenue hierarchy (district / mandal /
 * village), which the backend fills from OpenStreetMap when Google lacks it.
 */
export async function reverseGeocode(lat, lng, { signal, provider, admin } = {}) {
  // Most address forms reverse-geocode the point the user just picked from a
  // Google suggestion; that pick already brought the full address, so reuse it
  // instead of paying for a second lookup. (`admin` needs the OSM revenue
  // hierarchy the pick doesn't carry, so it always asks.)
  if (!admin && provider !== "osm") {
    const hit = recentPicks.find(
      (r) => Math.abs(r.lat - lat) < 1e-6 && Math.abs(r.lng - lng) < 1e-6
    );
    // Some forms need the pincode, which a picked landmark can lack; then the
    // full lookup below still runs.
    if (hit?.address?.pincode) {
      return { ...hit.address, location: { type: "Point", coordinates: [lng, lat] } };
    }
  }
  try {
    const out = await geoApi.reverse(lat, lng, { signal, provider, admin });
    return out?.address || null;
  } catch {
    return null;
  }
}

// The last few resolved Google picks (this tab only), for reverseGeocode.
const recentPicks = [];
const rememberPick = (lat, lng, address) => {
  if (!address) return;
  recentPicks.unshift({ lat, lng, address });
  recentPicks.length = Math.min(recentPicks.length, 5);
};

// Nominatim-order [south, north, west, east] array, the form the old Photon
// results carried in `raw.boundingbox` and callers still read.
const bboxArray = (b) => (b ? [b.south, b.north, b.west, b.east] : undefined);

const toSuggestion = (it) => ({
  id: it.id,
  provider: it.provider,
  display_name: it.description,
  title: it.title,
  subtitle: it.subtitle,
  // OpenStreetMap results arrive with coordinates; Google ones need
  // `resolvePlace` (one Place Details call that also ends the search session).
  lat: it.lat,
  lng: it.lng,
  address: it.state ? { state: it.state } : undefined,
  raw: { boundingbox: bboxArray(it.bbox), display_name: it.description },
});

/**
 * Forward search / autocomplete. Returns up to `limit` suggestions (the
 * backend sends up to 10: Google gives 5 a request, so it merges a plain and a
 * places-only request; see geo/google.js):
 *   [{ id, provider, display_name, title, subtitle, lat?, lng?, raw }]
 * `lat`/`lng` are present for OpenStreetMap results only; pass any suggestion
 * through `resolvePlace` before using its location. Pass the same `session` to
 * every keystroke of one search and to `resolvePlace` (see newSearchSession):
 * Google bills a finished session far below separate requests. Empty array for
 * short/empty queries or on error.
 */
export async function searchPlaces(query, { limit = 10, signal, provider, session, near } = {}) {
  const q = (query || "").trim();
  if (q.length < 3) return [];
  try {
    const out = await geoApi.autocomplete(q, {
      provider,
      session,
      lat: near?.lat,
      lng: near?.lng,
      signal,
    });
    return (out?.items || []).slice(0, limit).map(toSuggestion);
  } catch {
    return [];
  }
}

/**
 * A suggestion with its location filled in:
 *   { ...suggestion, lat, lng, raw: { boundingbox }, address }
 * `address` is the full app address shape for Google picks (null for
 * OpenStreetMap ones, which callers reverse-geocode as before). Returns null when
 * the place can't be resolved.
 */
export async function resolvePlace(suggestion, { session, signal } = {}) {
  if (!suggestion) return null;
  if (Number.isFinite(suggestion.lat) && Number.isFinite(suggestion.lng)) return suggestion;
  try {
    const p = await geoApi.place(suggestion.id, { session, signal });
    if (!p) return null;
    rememberPick(p.lat, p.lng, p.address);
    return {
      ...suggestion,
      lat: p.lat,
      lng: p.lng,
      address: p.address || null,
      raw: { ...suggestion.raw, boundingbox: bboxArray(p.bbox) },
    };
  } catch {
    return null;
  }
}

/**
 * Convenience: resolve a free-text address or a 6-digit pincode straight to
 * the app address shape (inside India). Returns `null` if nothing matches.
 */
export async function geocodeAddress(query, { signal, provider, state } = {}) {
  const q = (query || "").trim();
  if (!q) return null;
  try {
    const out = await geoApi.geocode(q, { signal, provider, state });
    return out?.address || null;
  } catch {
    return null;
  }
}

/**
 * Where a land-record place is, from whatever names a form already holds:
 * `{ village, mandal, district, city, pincode, state }`, any may be empty.
 * The backend tries the most specific combination first and widens until
 * something matches inside the given state. Resolves to
 * `{ lat, lng, zoom, bbox, level }` or `null`.
 */
export async function locatePlace(place = {}, { signal, provider } = {}) {
  try {
    const out = await geoApi.locate(
      {
        village: place.village || "",
        mandal: place.mandal || "",
        district: place.district || "",
        city: place.city || "",
        pincode: place.pincode || "",
        state: place.state || "",
      },
      { signal, provider }
    );
    return out?.place || null;
  } catch {
    return null;
  }
}
